import React from 'react';
import { useNotify } from 'react-admin';
import clipboardCopy from 'clipboard-copy';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { AccountBalance, FileCopyOutlined } from '@material-ui/icons';
import classNames from 'classnames';

import formatPrice from 'services/helpers/formatPrice';

import AchBalanceCheckButton from '../AchBalanceCheckButton';
import DetailsSectionCard, { SECTION_BORDER } from './DetailsSectionCard';
import Pill from './Pill';

const useStyles = makeStyles((theme) => ({
  columns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1.2fr)',
  },

  column: {
    padding: theme.spacing(2.5, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    '&:not(:last-child)': {
      borderRight: SECTION_BORDER,
    },
  },

  columnTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 'bold',
    lineHeight: '24px',
    textTransform: 'uppercase',
    color: theme.palette.text.primary,
  },

  columnCount: {
    color: theme.palette.secondary.contrastText,
  },

  keyValue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minHeight: 28,
  },

  label: {
    fontSize: 14,
    color: theme.palette.text.secondary,
  },

  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 13,
  },

  value: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'right',
  },

  method: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },

  copyButton: {
    padding: 4,
    color: theme.palette.secondary.contrastText,
  },

  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: 40,
    '&:not(:last-child)': {
      borderBottom: SECTION_BORDER,
    },
  },

  bankRow: {
    minHeight: 48,
  },

  brand: {
    minWidth: 44,
    height: 24,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 4,
    border: '1px solid #E4E5EE',
    backgroundColor: theme.palette.background.default,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#4F5467',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  digits: {
    flexGrow: 1,
    fontSize: 14,
    letterSpacing: '0.04em',
  },

  bankIcon: {
    fontSize: 20,
    color: theme.palette.secondary.contrastText,
  },

  bankInfo: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },

  bankName: {
    fontSize: 14,
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  bankDigits: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    letterSpacing: '0.04em',
  },

  empty: {
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
}));

const BRAND_SHORT_NAMES: Record<string, string> = {
  mastercard: 'MC',
  'american express': 'Amex',
  americanexpress: 'Amex',
  'diners club': 'Diners',
  dinersclub: 'Diners',
};

const getBrandLabel = (brand?: string) =>
  BRAND_SHORT_NAMES[String(brand ?? '').toLowerCase()] ?? brand;

// amount comes as an enum such as AUTO_REFILL_100; fall back to the raw value if it does not match
const formatAutoRefillAmount = (amount?: string | number | null) => {
  if (amount == null || amount === '') {
    return null;
  }

  const match = /^AUTO_REFILL_(\d+(?:\.\d+)?)$/i.exec(String(amount));
  if (match) {
    return formatPrice(match[1]);
  }

  return Number.isFinite(Number(amount)) ? formatPrice(Number(amount)) : String(amount);
};

const getAutoRefillMethod = (paymentSettings: any, cards: any[], bankAccounts: any[]) => {
  if (!paymentSettings) {
    return null;
  }

  const type = String(paymentSettings.paymentMethodType ?? '').toUpperCase();

  if (type === 'CARD') {
    const card = cards.find((item) => item.cardId === paymentSettings.defaultCardId);
    return card
      ? {
          badge: getBrandLabel(card.brand),
          title: card.brand,
          label: `•••• ${card.lastDigits}`,
        }
      : { badge: 'Card', title: 'Card', label: paymentSettings.defaultCardId ?? '—' };
  }

  if (type.includes('BANK')) {
    const account = bankAccounts.find(
      (item) => item.paymentMethodId === paymentSettings.defaultUsBankAccount
    );
    return account
      ? {
          badge: 'ACH',
          title: account.bankName,
          label: `${account.bankName} •••• ${account.lastDigits}`,
        }
      : { badge: 'ACH', title: 'Bank account', label: paymentSettings.defaultUsBankAccount ?? '—' };
  }

  return type ? { badge: type, title: type, label: '' } : null;
};

interface Props {
  userId: string;
  billingUserSettings: any;
}

const BillingInformationSection = ({ userId, billingUserSettings }: Props) => {
  const classes = useStyles();
  const notify = useNotify();

  const cards = billingUserSettings?.cards ?? [];
  const bankAccounts = billingUserSettings?.usBankAccounts ?? [];
  const autoRefill = billingUserSettings?.autoRefill;
  const stripeCustomerId = billingUserSettings?.stripeCustomerId;

  // Prefer top-level payment settings; use nested auto-refill values as fallbacks.
  const paymentMethodType = billingUserSettings?.paymentMethodType ?? autoRefill?.paymentMethodType;
  const defaultCardId = billingUserSettings?.defaultCardId ?? autoRefill?.defaultCardId;
  const defaultUsBankAccount =
    billingUserSettings?.defaultUsBankAccount ?? autoRefill?.defaultUsBankAccount;

  const handleCopyStripeId = () => {
    clipboardCopy(stripeCustomerId)
      .then(() => notify('Stripe customer ID is copied'))
      .catch((err) => notify(err.message, 'error'));
  };

  const autoRefillAmount = formatAutoRefillAmount(autoRefill?.amount);
  const autoRefillMethod = getAutoRefillMethod(
    { paymentMethodType, defaultCardId, defaultUsBankAccount },
    cards,
    bankAccounts
  );

  const summary = `${cards.length} payment card${cards.length === 1 ? '' : 's'} · ${
    bankAccounts.length
  } bank account${bankAccounts.length === 1 ? '' : 's'}`;

  return (
    <DetailsSectionCard id="user-details-billing" title="Billing Information" summary={summary}>
      {!billingUserSettings ? (
        <Typography className={classes.column} color="textSecondary">
          No billing information found
        </Typography>
      ) : (
        <div className={classes.columns}>
          <div className={classes.column}>
            <h3 className={classes.columnTitle}>Auto refill</h3>
            <div className={classes.keyValue}>
              <span className={classes.label}>Status</span>
              <Pill variant={autoRefill?.enabled ? 'success' : 'neutral'}>
                {autoRefill?.enabled ? 'Enabled' : 'Disabled'}
              </Pill>
            </div>
            {autoRefill?.enabled && (
              <>
                <div className={classes.keyValue}>
                  <span className={classes.label}>Amount</span>
                  <span className={classNames(classes.value, !autoRefillAmount && classes.label)}>
                    {autoRefillAmount ?? '—'}
                  </span>
                </div>
                <div className={classes.keyValue}>
                  <span className={classes.label}>Payment method</span>
                  {autoRefillMethod ? (
                    <span className={classes.method}>
                      <span className={classes.brand} title={autoRefillMethod.title}>
                        {autoRefillMethod.badge}
                      </span>
                      <span className={classes.value}>{autoRefillMethod.label}</span>
                    </span>
                  ) : (
                    <span className={classes.label}>—</span>
                  )}
                </div>
              </>
            )}
            <div className={classes.keyValue}>
              <span className={classes.label}>Stripe customer</span>
              {stripeCustomerId ? (
                <span className={classes.keyValue}>
                  <span className={classes.mono}>{stripeCustomerId}</span>
                  <IconButton
                    size="small"
                    aria-label="Copy Stripe customer ID"
                    className={classes.copyButton}
                    onClick={handleCopyStripeId}>
                    <FileCopyOutlined fontSize="small" />
                  </IconButton>
                </span>
              ) : (
                <span className={classes.label}>—</span>
              )}
            </div>
          </div>

          <div className={classes.column}>
            <h3 className={classes.columnTitle}>
              Payment cards <span className={classes.columnCount}>({cards.length})</span>
            </h3>
            {cards.length === 0 && <span className={classes.empty}>No payment cards</span>}
            {cards.map((card: any) => (
              <div key={card.cardId} className={classes.row}>
                <span className={classes.brand} title={card.brand}>
                  {getBrandLabel(card.brand)}
                </span>
                <span className={classes.digits}>•••• {card.lastDigits}</span>
                {card.failedLastTime && (
                  <Pill variant="danger" small>
                    Failed
                  </Pill>
                )}
                {card.cardId === defaultCardId && (
                  <Pill variant="primary" small>
                    Default
                  </Pill>
                )}
              </div>
            ))}
          </div>

          <div className={classes.column}>
            <h3 className={classes.columnTitle}>
              Bank accounts <span className={classes.columnCount}>({bankAccounts.length})</span>
            </h3>
            {bankAccounts.length === 0 && <span className={classes.empty}>No bank accounts</span>}
            {bankAccounts.map((account: any) => (
              <div
                key={account.paymentMethodId}
                className={classNames(classes.row, classes.bankRow)}>
                <AccountBalance className={classes.bankIcon} />
                <span className={classes.bankInfo}>
                  <span className={classes.bankName}>
                    {account.bankName}
                    {account.failedLastTime && (
                      <Pill variant="danger" small>
                        Failed
                      </Pill>
                    )}
                    {account.paymentMethodId === defaultUsBankAccount && (
                      <Pill variant="primary" small>
                        Default
                      </Pill>
                    )}
                  </span>
                  <span className={classes.bankDigits}>•••• {account.lastDigits}</span>
                </span>
                <AchBalanceCheckButton
                  userId={userId}
                  paymentMethodId={account.paymentMethodId}
                  financialConnectionsAccount={account.financialConnectionsAccount}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </DetailsSectionCard>
  );
};

export default BillingInformationSection;
